import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { EnquiryDetail, SubmitEnquiryOffer } from '../../services/sellOldAC';
import { GETAmcRequestById } from '../../services/requests';
import { GETConsultancyRequestById } from '../../services/consultancy';
import useSnackbar from '../../hooks/useSnackbar';

type QuoteSource = 'OLD_AC' | 'COPPER_PIPING' | 'AMC' | 'FREE_CONSULTATION';

// Hiding the native spin buttons only removes the visual arrows — the
// browser still steps a focused number input by 1 on ArrowUp/ArrowDown
// key presses. Block just those two keys so amounts can only change by
// typing.
const blockNumberArrowKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
  }
};

interface AddQuotationProps {
  open: boolean;
  onClose: () => void;
  id: string;
  source: QuoteSource;
  enquiryId?: string;
  onSaved?: () => void;
}

interface ItemForm {
  key: string;
  brand: string;
  quantity: string;
  age: string;
  condition: string;
  inspectionRemarks: string;
  offeredAmount: string;
}

const makeItem = (overrides: Partial<ItemForm> = {}): ItemForm => ({
  key: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  brand: '',
  quantity: '1',
  age: '',
  condition: '',
  inspectionRemarks: '',
  offeredAmount: '',
  ...overrides,
});

// The itemized quotation form was originally built for AMC (valuing/quoting
// individual AC units — Brand/Age/Condition are meaningful there) and then
// reused as-is for Copper Piping, where Brand/Age/Condition don't apply to a
// piping job. Free Consultancy uses the same full field set as AMC — a
// free-consultation visit is still an on-site inspection of a customer's
// AC(s), so the admin needs to record the same details (brand, quantity,
// condition, age, inspection remarks) before quoting.
const ITEM_FIELD_CONFIG: Record<
  QuoteSource,
  {
    itemLabel: string;
    showBrand: boolean;
    showQuantity: boolean;
    quantityLabel: string;
    showCondition: boolean;
    showAge: boolean;
    remarksLabel: string;
  }
> = {
  AMC: {
    itemLabel: 'AC',
    showBrand: true,
    showQuantity: true,
    quantityLabel: 'No. of AC',
    showCondition: true,
    showAge: true,
    remarksLabel: 'Inspection Remarks',
  },
  COPPER_PIPING: {
    itemLabel: 'Item',
    showBrand: false,
    showQuantity: true,
    quantityLabel: 'Quantity',
    showCondition: false,
    showAge: false,
    remarksLabel: 'Work Remarks',
  },
  FREE_CONSULTATION: {
    itemLabel: 'AC',
    showBrand: true,
    showQuantity: true,
    quantityLabel: 'No. of AC',
    showCondition: true,
    showAge: true,
    remarksLabel: 'Inspection Remarks',
  },
  OLD_AC: {
    itemLabel: 'AC',
    showBrand: true,
    showQuantity: true,
    quantityLabel: 'No. of AC',
    showCondition: true,
    showAge: true,
    remarksLabel: 'Inspection Remarks',
  },
};

// AMC and Copper Piping requests already list the AC types/quantities the
// customer asked about — seed one line item per entry (quantity pre-filled,
// everything else left for the admin to fill in during inspection). Free
// Consultancy has no such list, so it starts with a single blank item.
const buildInitialItems = (source: QuoteSource, request: any): ItemForm[] => {
  if (source === 'AMC') {
    const acDetails = request?.acDetails ?? [];
    if (acDetails.length > 0) {
      return acDetails.map((ac: any, index: number) =>
        makeItem({
          key: ac._id || `amc-${index}`,
          quantity: String(ac.quantity ?? 1),
        }),
      );
    }
  }

  if (source === 'COPPER_PIPING') {
    const acTypes = request?.copperPipingDetails?.acTypes ?? [];
    if (acTypes.length > 0) {
      return acTypes.map((ac: any, index: number) =>
        makeItem({
          key: `cp-${index}`,
          quantity: String(ac.quantity ?? 1),
        }),
      );
    }
  }

  if (source === 'FREE_CONSULTATION') {
    // Seed one line item pre-filled with what the customer already told us
    // (brand + quantity) so the admin only has to fill in condition/age/
    // remarks/amount during inspection, same as AMC.
    return [
      makeItem({
        key: 'fc-0',
        brand: request?.brand?.name || '',
        quantity: String(request?.quantity || 1),
      }),
    ];
  }

  return [makeItem({ key: 'item-0' })];
};

// Best-effort address/notes/photos lookups across the different request
// shapes (AMC lead, Enquiry-based Copper Piping/Old AC, Consultancy) so the
// "Request Details" panel below can show whatever each source actually has
// without needing a bespoke renderer per source.
const composeAddress = (address?: any): string => {
  if (!address) return '';
  const parts = [
    address.house,
    address.street,
    address.landmark,
    address.city,
    address.state,
    address.zipcode,
  ].filter(Boolean);
  return parts.join(', ');
};

const AddQuotation = ({
  open,
  onClose,
  id,
  source,
  enquiryId: enquiryIdProp,
  onSaved,
}: AddQuotationProps) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState<any>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [items, setItems] = useState<ItemForm[]>([]);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    if (!open || !id) return;
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, source]);

  const enquiryId = useMemo(() => {
    if (source === 'AMC') return request?.enquiry?._id || enquiryIdProp || '';
    if (source === 'FREE_CONSULTATION')
      return request?.enquiryId || enquiryIdProp || '';
    return request?._id || enquiryIdProp || id;
  }, [id, request, source, enquiryIdProp]);

  // Whoever is already assigned as the inspector/technician on this request.
  // Shown read-only — there's one inspector per visit, not per AC.
  const inspectionAgentName = useMemo(() => {
    if (source === 'COPPER_PIPING' || source === 'OLD_AC') {
      return request?.technician_info?.name || '';
    }
    return request?.assignedTechnician?.name || '';
  }, [request, source]);

  const requestAddress = useMemo(
    () =>
      composeAddress(request?.addressDetails) ||
      request?.address ||
      request?.inspectionAddress ||
      composeAddress(request?.enquiry?.addressDetails) ||
      '',
    [request],
  );

  const requestNotes = useMemo(
    () =>
      request?.comment ||
      request?.copperPipingDetails?.additionalNotes ||
      request?.additionalNotes ||
      '',
    [request],
  );

  const requestServices = useMemo(
    () =>
      (request?.serviceName ?? [])
        .map((service: any) =>
          typeof service === 'string'
            ? service
            : (service?.name ??
              service?.serviceName ??
              service?.title ??
              service?.label ??
              ''),
        )
        .filter(Boolean),
    [request],
  );

  // Only Free Consultancy stores photos today — this naturally stays empty
  // (and the section hides) for every other source.
  const requestPhotos = useMemo(() => {
    if (request?.photos && request.photos.length > 0)
      return request.photos as string[];
    if (request?.documentURL) return [request.documentURL as string];
    return [] as string[];
  }, [request]);

  const hasRequestDetails =
    Boolean(requestAddress) ||
    Boolean(requestNotes) ||
    requestServices.length > 0 ||
    requestPhotos.length > 0;

  const oldAcDetails = request?.oldAcDetails ?? [];
  const isOldAc = source === 'OLD_AC';
  // AMC / Copper Piping / Free Consultancy all use the itemized (per-AC)
  // quotation form; Old AC keeps its existing simpler layout.
  const isItemized = !isOldAc;
  const itemFields = ITEM_FIELD_CONFIG[source];

  async function fetchRequest() {
    try {
      setLoading(true);
      let data: any = null;

      if (source === 'AMC') {
        data = await GETAmcRequestById(id);
      } else if (source === 'FREE_CONSULTATION') {
        data = await GETConsultancyRequestById(id);
      } else {
        const response = await EnquiryDetail(enquiryIdProp || id);
        data = response.data;
      }

      setRequest(data);
      setAmounts({});
      if (source !== 'OLD_AC') {
        setItems(buildInitialItems(source, data));
      }
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Unable to load request',
        'error',
      );
    } finally {
      setLoading(false);
    }
  }

  const handleOldAcAmountChange = (key: string, value: string) => {
    setAmounts((prev) => ({ ...prev, [key]: value }));
  };

  const updateItem = (key: string, field: keyof ItemForm, value: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addItem = () => setItems((prev) => [...prev, makeItem()]);

  const removeItem = (key: string) =>
    setItems((prev) =>
      prev.length > 1 ? prev.filter((item) => item.key !== key) : prev,
    );

  const finishUp = () => {
    onSaved?.();
    setTimeout(() => onClose(), 700);
  };

  const handleSubmit = async () => {
    if (!enquiryId) {
      showSnackbar('Enquiry id is missing', 'error');
      return;
    }

    if (isOldAc) {
      const offers = oldAcDetails.map((ac: any, index: number) => ({
        index,
        offeredAmount: Number(amounts[ac._id] || 0),
      }));

      if (
        offers.some(
          (offer: any) =>
            Number.isNaN(offer.offeredAmount) || offer.offeredAmount < 0,
        )
      ) {
        showSnackbar('Enter valid offer amount for each AC', 'error');
        return;
      }

      try {
        setSubmitting(true);
        await SubmitEnquiryOffer({ enquiryId, source, offers });
        showSnackbar('Quotation submitted successfully', 'success');
        finishUp();
      } catch (error) {
        showSnackbar(
          error instanceof Error ? error.message : 'Failed to submit quotation',
          'error',
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Itemized flow (AMC / Copper Piping / Free Consultancy)
    const numericItems = items.map((item) => ({
      brand: item.brand,
      quantity: Number(item.quantity) || 1,
      age: item.age,
      condition: item.condition,
      inspectionRemarks: item.inspectionRemarks,
      offeredAmount: Number(item.offeredAmount),
    }));

    if (
      numericItems.length === 0 ||
      numericItems.some(
        (item) => Number.isNaN(item.offeredAmount) || item.offeredAmount < 0,
      )
    ) {
      showSnackbar('Enter a valid offer amount for each AC', 'error');
      return;
    }

    try {
      setSubmitting(true);
      await SubmitEnquiryOffer({
        enquiryId,
        source,
        items: numericItems,
        inspectionAgent: inspectionAgentName,
      });
      showSnackbar('Quotation submitted successfully', 'success');
      finishUp();
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to submit quotation',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Renders as a real modal (backdrop + elevated card) inline on whatever
  // request-details screen opened it — there's no separate
  // "/amc/add-quotation/:id" route anymore, so the underlying page (and its
  // data) stays mounted behind it instead of being swapped out.
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1a1a1a',
          borderRadius: 3,
          border: '1px solid #2a2a2a',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        },
      }}
    >
      {SnackbarComponent}

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#fff',
          fontSize: 22,
          fontWeight: 700,
          pb: 2,
        }}
      >
        Add Quotation
        <IconButton
          onClick={onClose}
          sx={{ color: '#9e9e9e' }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogContent sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#E31E25' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <InfoRow label="Request Type" value={source.replaceAll('_', ' ')} />
            <InfoRow
              label="Customer"
              value={
                request?.user_info?.name ||
                request?.userDetails?.name ||
                request?.username ||
                'N/A'
              }
            />

            {isItemized && (
              <InfoRow
                label="Inspection Agent"
                value={inspectionAgentName || 'Not assigned yet'}
              />
            )}

            {/* What the customer actually submitted — address, services,
                notes, and any photos — so the admin isn't quoting blind. */}
            {hasRequestDetails && (
              <Box
                sx={{
                  background: '#202020',
                  borderRadius: 2,
                  border: '1px solid #2a2a2a',
                  p: 2.5,
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 600, mb: 1.5 }}>
                  Request Details
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {requestAddress && (
                    <InfoRow label="Address" value={requestAddress} />
                  )}
                  {requestServices.length > 0 && (
                    <InfoRow
                      label="Services"
                      value={requestServices.join(', ')}
                    />
                  )}
                  {requestNotes && (
                    <Box>
                      <Typography
                        sx={{ color: '#9e9e9e', fontSize: 13, mb: 0.5 }}
                      >
                        Comment / Notes
                      </Typography>
                      <Typography sx={{ color: '#ddd' }}>
                        {requestNotes}
                      </Typography>
                    </Box>
                  )}
                  {requestPhotos.length > 0 && (
                    <Box>
                      <Typography
                        sx={{ color: '#9e9e9e', fontSize: 13, mb: 1 }}
                      >
                        Photos
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {requestPhotos.map((url, index) => (
                          <Box
                            key={`${url}-${index}`}
                            component="img"
                            src={url}
                            alt={`Request photo ${index + 1}`}
                            onClick={() => window.open(url, '_blank')}
                            sx={{
                              width: 100,
                              height: 100,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #3a3a3a',
                              cursor: 'pointer',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {isOldAc && oldAcDetails.length > 0
              ? oldAcDetails.map((ac: any, index: number) => (
                  <Box
                    key={ac._id || index}
                    sx={{ background: '#232323', borderRadius: 1, p: 2 }}
                  >
                    <Typography
                      sx={{ color: '#fff', fontWeight: 600, mb: 1.5 }}
                    >
                      AC {index + 1}: {ac.brand || 'Brand N/A'} {ac.model || ''}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                      }}
                    >
                      <InfoRow
                        label="Type"
                        value={ac.acType || 'N/A'}
                        compact
                      />
                      <InfoRow label="Age" value={ac.age || 'N/A'} compact />
                      <DarkInput
                        label="Offer Amount"
                        type="number"
                        value={amounts[ac._id] || ''}
                        onChange={(value) =>
                          handleOldAcAmountChange(ac._id, value)
                        }
                      />
                    </Box>
                  </Box>
                ))
              : null}

            {isItemized &&
              items.map((item, index) => (
                <Box
                  key={item.key}
                  sx={{
                    background: '#232323',
                    borderRadius: 2,
                    p: 2.5,
                    position: 'relative',
                    border: '1px solid #2f2f2f',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                      {itemFields.itemLabel} {index + 1}
                    </Typography>
                    {items.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeItem(item.key)}
                        sx={{ color: '#9e9e9e' }}
                        aria-label={`Remove ${itemFields.itemLabel}`}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Divider sx={{mb:2}} />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2.5,
                      mb: 2.5,
                    }}
                  >
                    {itemFields.showBrand && (
                      <DarkInput
                        label="AC Brand"
                        value={item.brand}
                        onChange={(value) =>
                          updateItem(item.key, 'brand', value)
                        }
                      />
                    )}
                    <DarkInput
                      label={itemFields.remarksLabel}
                      value={item.inspectionRemarks}
                      onChange={(value) =>
                        updateItem(item.key, 'inspectionRemarks', value)
                      }
                    />
                    {itemFields.showQuantity && (
                      <DarkInput
                        label={itemFields.quantityLabel}
                        type="number"
                        value={item.quantity}
                        onChange={(value) =>
                          updateItem(item.key, 'quantity', value)
                        }
                      />
                    )}
                    {itemFields.showCondition && (
                      <DarkInput
                        label="AC Condition"
                        value={item.condition}
                        onChange={(value) =>
                          updateItem(item.key, 'condition', value)
                        }
                      />
                    )}
                    {itemFields.showAge && (
                      <DarkInput
                        label="AC Age"
                        value={item.age}
                        onChange={(value) => updateItem(item.key, 'age', value)}
                      />
                    )}
                  </Box>

                  <EditableAmount
                    value={item.offeredAmount}
                    onChange={(value) =>
                      updateItem(item.key, 'offeredAmount', value)
                    }
                  />
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>

      {!loading && (
        <>
          <Divider sx={{ borderColor: '#2a2a2a' }} />
          {/* Actions live bottom-right, together, like a standard dialog
              footer — "+ Add {item}" is the secondary action, the
              submit/"Done" button is primary and sits furthest right. */}
          <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
            {isItemized && (
              <Button
                variant="outlined"
                onClick={addItem}
                sx={{
                  color: '#E31E25',
                  borderColor: '#E31E25',
                  borderRadius: '10px',
                  px: 2.5,
                  '&:hover': {
                    borderColor: '#B71C1C',
                    backgroundColor: 'rgba(227, 30, 37, 0.08)',
                  },
                }}
              >
                + Add {itemFields.itemLabel}
              </Button>
            )}

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                background: '#E31E25',
                color: '#fff',
                px: 4,
                py: 1.1,
                borderRadius: '10px',
                fontWeight: 600,
                '&:hover': { background: '#C62828' },
                '&.Mui-disabled': { background: '#7a2a2a', color: '#ddd' },
              }}
            >
              {submitting
                ? 'Submitting…'
                : isItemized
                  ? 'Done'
                  : 'Submit Quotation'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const InfoRow = ({
  label,
  value,
  compact = false,
}: {
  label: string;
  value: string;
  compact?: boolean;
}) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
    <Typography
      sx={{ color: '#9e9e9e', width: compact ? 80 : 140, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: '#fff' }}>{value}</Typography>
  </Box>
);

const DarkInput = ({
  label,
  type = 'text',
  value,
  onChange,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <TextField
    label={label}
    type={type}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    // Number inputs otherwise capture the mouse wheel and silently
    // increment/decrement the value while the admin is just scrolling the
    // page — blur on wheel so scrolling never changes the amount.
    onWheel={
      type === 'number'
        ? (event) => (event.target as HTMLElement).blur()
        : undefined
    }
    onKeyDown={type === 'number' ? blockNumberArrowKeys : undefined}
    fullWidth
    variant="outlined"
    InputLabelProps={{ sx: { color: '#9e9e9e' } }}
    InputProps={{
      sx: { color: '#fff', background: '#1c1c1c', borderRadius: '8px' },
    }}
    sx={{
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#5a5a5a' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8a8a8a' },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E31E25',
      },
      '& input[type=number]': { MozAppearance: 'textfield' },
      '& input[type=number]::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '& input[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    }}
  />
);

// Displays the offer amount as large, prominent text (matching the
// requested design) that becomes an editable field when the pencil icon is
// clicked.
const EditableAmount = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const [editing, setEditing] = useState(false);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#9e9e9e', fontSize: 14 }}>
          Offer Amount
        </Typography>
        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          sx={{ color: '#E31E25', p: 0.25 }}
          aria-label="Edit offer amount"
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {editing ? (
        <TextField
          autoFocus
          type="number"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => setEditing(false)}
          onWheel={(event) => (event.target as HTMLElement).blur()}
          onKeyDown={blockNumberArrowKeys}
          variant="standard"
          placeholder="0"
          InputProps={{
            disableUnderline: false,
            sx: { color: '#fff', fontSize: 30, fontWeight: 700 },
          }}
          sx={{
            mt: 0.5,
            '& .MuiInput-underline:before': { borderColor: '#5a5a5a' },
            '& input[type=number]': { MozAppearance: 'textfield' },
            '& input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }}
        />
      ) : (
        <Typography
          sx={{ color: '#fff', fontSize: 32, fontWeight: 700, mt: 0.5 }}
        >
          ₹{value ? Number(value).toLocaleString('en-IN') : '0'}
        </Typography>
      )}
    </Box>
  );
};

export default AddQuotation;
